export default function OrgChecklistReportCard({ check, isDarkMode }) {
    let items = [];
    try {
        const parsed = check.ResultsJSON ? JSON.parse(check.ResultsJSON) : null;
        items = Array.isArray(parsed?.orgChecklist) ? parsed.orgChecklist : [];
    } catch { /* malformed JSON */ }

    if (!items.length) return null;

    const passCount = items.filter((i) => i.status === 'PASS').length;
    const total = items.length;
    const allPass = passCount === total;
    const submitted = check.CreatedOn ? new Date(check.CreatedOn).toLocaleDateString() : null;
    const resumeLabel = check.ResumeLabel || check.FileName || '';
    const orgLabel = check.OrgName || check.OrgCode || 'Organization';

    const heading = isDarkMode ? 'text-slate-100' : 'text-slate-900';
    const sub = isDarkMode ? 'text-slate-400' : 'text-slate-600';
    const card = isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
    const passPill = isDarkMode ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700';
    const failPill = isDarkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-700';
    const partialPill = isDarkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700';

    const esc = (s) => {
        if (typeof s !== 'string') return String(s ?? '');
        return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    };

    const handlePrint = () => {
        const w = window.open('', '_blank');
        w.document.write(`<html><head><title>${esc(orgLabel)} Resume Checklist — Pono Pilot</title>
            <style>body{font-family:Arial,sans-serif;max-width:800px;margin:0 auto;padding:24px;color:#1e293b}
            h1{font-size:22px;margin:0 0 4px}
            .meta{color:#64748b;font-size:13px;margin:0}
            .summary{display:inline-block;margin:10px 0 16px;padding:4px 12px;border-radius:99px;font-size:13px;font-weight:600}
            .card{border:1px solid #e2e8f0;border-radius:12px;padding:4px 16px}
            .item{display:flex;align-items:flex-start;gap:12px;padding:10px 0;border-bottom:1px solid #f1f5f9}
            .item:last-child{border-bottom:0}
            .badge{flex:0 0 46px;text-align:center;padding:3px 0;border-radius:99px;font-size:10.5px;font-weight:700;letter-spacing:.3px;margin-top:1px}
            .pass{background:#d1fae5;color:#065f46}.fail{background:#fee2e2;color:#991b1b}.partial{background:#fef3c7;color:#92400e}
            .item-text{flex:1;min-width:0}
            .item-title{font-size:13.5px;font-weight:600;color:#0f172a;line-height:1.35}
            .item-note{font-size:12.5px;color:#475569;margin-top:3px;line-height:1.4}
            .footer{margin-top:28px;padding-top:14px;border-top:1px solid #e2e8f0;text-align:center;color:#94a3b8;font-size:11px}
            @media print{body{padding:12px}.item{break-inside:avoid}}</style></head><body>`);
        w.document.write(`<h1>Resume Checklist Results</h1>`);
        w.document.write(`<p class="meta">${esc(orgLabel)} &middot; ${esc(resumeLabel)}${submitted ? ' &middot; Checked ' + submitted : ''}</p>`);
        w.document.write(`<span class="summary ${allPass ? 'pass' : 'partial'}">${passCount} of ${total} standards met</span>`);
        w.document.write(`<div class="card">`);
        items.forEach((item) => {
            const cls = item.status === 'PASS' ? 'pass' : 'fail';
            w.document.write(`<div class="item"><span class="badge ${cls}">${esc(item.status)}</span><div class="item-text"><div class="item-title">${esc(item.item)}</div>${item.explanation ? `<div class="item-note">${esc(item.explanation)}</div>` : ''}</div></div>`);
        });
        w.document.write(`</div>`);
        w.document.write(`<div class="footer">Powered by Pono Pilot &middot; ponopilot.com</div>`);
        w.document.write(`</body></html>`);
        w.document.close();
        setTimeout(() => w.print(), 300);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <button
                    onClick={handlePrint}
                    className="shrink-0 w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors text-center order-2 sm:order-1"
                >
                    Download PDF
                </button>
                <div className="min-w-0 text-left sm:text-right order-1 sm:order-2">
                    <h1 className={`text-xl sm:text-2xl md:text-3xl font-extrabold ${heading}`}>
                        Resume Checklist Results
                    </h1>
                    <p className={`mt-1 text-sm break-words ${sub}`}>
                        {orgLabel} &middot; {resumeLabel} {submitted ? `· Checked ${submitted}` : ''}
                    </p>
                </div>
            </div>

            <div className={`rounded-2xl border p-5 md:p-6 ${card}`}>
                <div className="flex items-center justify-between gap-3 mb-4">
                    <h2 className={`text-lg font-bold ${heading}`}>{orgLabel} Standards</h2>
                    <span className={`shrink-0 text-xs font-semibold px-3 py-1 rounded-full ${allPass ? passPill : partialPill}`}>
                        {passCount} of {total} met
                    </span>
                </div>
                <div className="space-y-3">
                    {items.map((item, i) => (
                        <div key={i} className={`flex items-start gap-3 rounded-lg p-3 ${
                            isDarkMode ? 'bg-slate-900/50' : 'bg-slate-50'
                        }`}>
                            <span className={`shrink-0 mt-0.5 text-xs font-bold px-2 py-0.5 rounded-full ${
                                item.status === 'PASS' ? passPill : failPill
                            }`}>
                                {item.status}
                            </span>
                            <div className="min-w-0">
                                <p className={`text-sm font-medium ${heading}`}>{item.item}</p>
                                {item.explanation && (
                                    <p className={`text-xs mt-0.5 ${sub}`}>{item.explanation}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
